import { ChangeDetectionStrategy, Component, Injectable, computed, effect, inject, signal, untracked } from "@angular/core";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatDivider } from "@angular/material/divider";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { FormsModule } from "@angular/forms";

import { DebugModeService } from "../../../services/debug-mode.service";
import { MockDataService } from "../../../services/mock-data.service";
import { HitlService } from "../../../services/hitl.service";
import { BackgroundJobTrackerService } from "../../../services/background-job-tracker.service";
import { BlackboardStreamService } from "../../../services/blackboard-stream.service";
import { EvidenceProgressStore } from "../../../services/evidence-progress-store.service";
import { CardGridComponent } from "../../content/shared/card-grid/card-grid.component";
import { ContentBlockCardComponent } from "../../content/cards/content-block-card/content-block-card.component";
import { MapLayerCardComponent } from "../../content/cards/map-layer-card/map-layer-card.component";
import { PlanCardComponent } from "../../content/cards/plan-card/plan-card.component";
import { PlanProgressCardComponent } from "../../content/cards/plan-progress-card/plan-progress-card.component";
import { EvidenceProgressCardComponent } from "../../content/cards/evidence-progress-card/evidence-progress-card.component";
import { GeoHintChipComponent } from "../../content/inline/geo-hint-chip/geo-hint-chip.component";
import { WorkareaHintChipComponent } from "../../content/inline/workarea-hint-chip/workarea-hint-chip.component";
import { TaskListCardComponent } from "../../content/cards/task-list-card/task-list-card.component";
import { BackgroundJobPanelComponent } from "../../background-job-panel/background-job-panel.component";
import { HitlConfirmCardComponent } from "../../hitl/hitl-confirm-card/hitl-confirm-card.component";
import { HitlSelectionCardComponent } from "../../hitl/hitl-selection-card/hitl-selection-card.component";
import { HitlFormCardComponent } from "../../hitl/hitl-form-card/hitl-form-card.component";
import { HitlToolCallCardComponent } from "../../hitl/hitl-tool-call-card/hitl-tool-call-card.component";
import { ReasoningStepComponent } from "../../chat-assistant-message/reasoning-step/reasoning-step.component";
import { ToolCallHintComponent } from "../../content/inline/tool-call-hint/tool-call-hint.component";
import type { InterruptItem, HitlResumePayload, InterruptPayload } from "../../../model/interrupt.types";
import type { BackgroundJobBlockData, BackgroundJobSnapshot } from "../../../model/block.types";

/** No-op HitlService scoped to the mock panel — prevents mock cards from affecting real HITL state. */
class NoopHitlService {
  readonly currentEntry = signal<InterruptItem | null>(null);

  readonly isBlocked = computed(() => false);

  readonly displayText = computed(() => "");

  readonly showProgress = computed(() => false);

  readonly currentStep = computed(() => 0);

  readonly totalEntries = computed(() => 0);

  readonly frontendToolsPayload = computed(() => null as InterruptPayload | null);

  activate(_p: InterruptPayload): void {}

  clear(): void {}

  clearFrontendTools(): void {}

  setPending(_p: InterruptPayload): void {}

  submitAnswer(_v: string | string[]): HitlResumePayload | null {
    return null;
  }

  resolve(): void {}
}

/** Mock BackgroundJobTrackerService — returns static snapshots from MockDataService. */
@Injectable()
class MockBackgroundJobTrackerService {
  private readonly mocks = inject(MockDataService);

  /** All enabled bg-job snapshots shown in the floating panel. */
  readonly activeJobs = computed(() => this.mocks.enabledBgJobMocks());

  getSnapshot(jobId: string): BackgroundJobSnapshot | null {
    return this.mocks.bgJobItems.find((i) => i.snapshot.block.job_id === jobId)?.snapshot ?? null;
  }

  startJob(_block: BackgroundJobBlockData): void {}
}

/**
 * Debug-only collapsible panel for rendering mock AI cards without backend calls.
 * Visible only when DebugModeService.isDebugMode() is true.
 * Each scenario can be independently toggled via checkboxes.
 */
@Component({
  selector: "app-debug-mock-panel",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButton,
    MatIconButton,
    MatCheckbox,
    MatDivider,
    MatIcon,
    MatTooltip,
    CardGridComponent,
    ContentBlockCardComponent,
    MapLayerCardComponent,
    PlanCardComponent,
    PlanProgressCardComponent,
    EvidenceProgressCardComponent,
    GeoHintChipComponent,
    WorkareaHintChipComponent,
    TaskListCardComponent,
    BackgroundJobPanelComponent,
    HitlConfirmCardComponent,
    HitlSelectionCardComponent,
    HitlFormCardComponent,
    HitlToolCallCardComponent,
    ReasoningStepComponent,
    ToolCallHintComponent,
  ],
  providers: [
    { provide: HitlService, useClass: NoopHitlService },
    { provide: BackgroundJobTrackerService, useClass: MockBackgroundJobTrackerService },
    // Panel-local stream + evidence stores (same pattern as the NoopHitlService override): the
    // evidence card inside the panel renders the MOCK episodes pushed below instead of joining
    // the real chat-container store, and the plan cards see no live stream (they fall back to
    // the durable plan input / `run_engine.task_executions` — exactly the surfaces under mock).
    BlackboardStreamService,
    EvidenceProgressStore,
  ],
  templateUrl: "./debug-mock-panel.component.html",
  styleUrl: "./debug-mock-panel.component.scss",
})
export class DebugMockPanelComponent {
  protected readonly debugMode = inject(DebugModeService);

  protected readonly mocks = inject(MockDataService);

  /** Panel-local evidence store (see providers) — receives the replayed mock episodes. */
  readonly #evidence = inject(EvidenceProgressStore);

  protected readonly isPanelOpen = signal(false);

  protected readonly enabledToolMocks = this.mocks.enabledToolMocks;

  protected readonly enabledHitlMocks = this.mocks.enabledHitlMocks;

  protected readonly enabledBgJobMocks = this.mocks.enabledBgJobMocks;

  protected readonly enabledReasoningMocks = this.mocks.enabledReasoningMocks;

  protected readonly enabledMapLayerMocks = this.mocks.enabledMapLayerMocks;

  protected readonly enabledToolCallMocks = this.mocks.enabledToolCallMocks;

  protected readonly enabledPlanMocks = this.mocks.enabledPlanMocks;

  protected readonly enabledPlanCardMocks = computed(() => this.enabledPlanMocks().filter((m) => m.view === "card"));

  protected readonly enabledPlanProgressMocks = computed(() =>
    this.enabledPlanMocks().filter((m) => m.view === "progress"),
  );

  protected readonly evidenceEnabled = computed(() => this.mocks.enabledEvidenceMocks().length > 0);

  protected readonly enabledGeoChipMocks = this.mocks.enabledGeoChipMocks;

  protected readonly enabledWorkareaChipMocks = this.mocks.enabledWorkareaChipMocks;

  protected readonly enabledCount = this.mocks.enabledCount;

  protected readonly hasEnabled = this.mocks.hasEnabled;

  constructor() {
    // Evidence-progress toggle: whenever an evidence mock entry is enabled, replay its full
    // `evidence_progress` envelope sequence into the panel-local store (clear first so
    // re-enabling starts a fresh episode stack); disabling clears the store.
    effect(() => {
      const enabled = this.mocks.enabledEvidenceMocks();
      untracked(() => {
        this.#evidence.clear();
        for (const item of enabled) {
          for (const event of item.events) this.#evidence.store(event);
        }
      });
    });
  }

  protected togglePanel(): void {
    this.isPanelOpen.update((v) => !v);
  }

  protected disableAll(): void {
    [
      ...this.mocks.toolItems,
      ...this.mocks.hitlItems,
      ...this.mocks.bgJobItems,
      ...this.mocks.reasoningItems,
      ...this.mocks.mapLayerItems,
      ...this.mocks.toolCallItems,
      ...this.mocks.planItems,
      ...this.mocks.evidenceItems,
      ...this.mocks.geoChipItems,
      ...this.mocks.workareaChipItems,
    ].forEach((i) => i.enabled.set(false));
  }

  protected onHitlSubmit(_value: string | string[]): void {
    // no-op for mock cards
  }
}
